import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { getFreshToken } from '@/lib/auth-token';

interface CronJob {
  id: string;
  nome: string;
  descricao: string | null;
  schedule: string;
  edge_function: string;
  ativo: boolean;
  ultima_execucao: string | null;
  ultimo_resultado: string | null;
  ultimo_sucesso: boolean | null;
  criado_em: string;
}

const EDGE_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/executar-tarefa`;

export default function CronJobsTab() {
  const [jobs, setJobs] = useState<CronJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [editJob, setEditJob] = useState<CronJob | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('cron_jobs')
      .select('*')
      .order('nome');
    if (err) {
      setError('Erro ao carregar tarefas: ' + err.message);
    } else {
      setJobs(data ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleToggle = async (job: CronJob) => {
    setError('');
    setMsg('');
    const { error: err } = await supabase
      .from('cron_jobs')
      .update({ ativo: !job.ativo, atualizado_em: new Date().toISOString() })
      .eq('id', job.id);
    if (err) {
      setError('Erro ao alterar estado: ' + err.message);
    } else {
      setMsg(`Tarefa "${job.nome}" ${!job.ativo ? 'ativada' : 'desativada'} com sucesso.`);
      await fetchJobs();
      setTimeout(() => setMsg(''), 4000);
    }
  };

  const handleExecute = async (job: CronJob) => {
    setError('');
    setMsg('');
    setExecuting(job.id);
    try {
      const token = await getFreshToken();
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tarefa: job.edge_function, action: 'manual_trigger' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? json.resultado?.error ?? `Erro HTTP ${res.status}`);

      await supabase
        .from('cron_jobs')
        .update({
          ultima_execucao: new Date().toISOString(),
          ultimo_resultado: json.message ?? 'Executado manualmente',
          ultimo_sucesso: true,
          atualizado_em: new Date().toISOString(),
        })
        .eq('id', job.id);

      setMsg(`Tarefa "${job.nome}" executada com sucesso!`);
      await fetchJobs();
      setTimeout(() => setMsg(''), 4000);
    } catch (err: unknown) {
      const msgErr = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(`Erro ao executar "${job.nome}": ${msgErr}`);
      await supabase
        .from('cron_jobs')
        .update({
          ultima_execucao: new Date().toISOString(),
          ultimo_resultado: msgErr,
          ultimo_sucesso: false,
          atualizado_em: new Date().toISOString(),
        })
        .eq('id', job.id);
      await fetchJobs();
      setTimeout(() => setError(''), 6000);
    } finally {
      setExecuting(null);
    }
  };

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editJob) return;
    setError('');
    setMsg('');

    const formData = new FormData(e.currentTarget);
    const data = {
      nome: String(formData.get('nome') ?? '').trim(),
      descricao: String(formData.get('descricao') ?? '').trim() || null,
      schedule: String(formData.get('schedule') ?? '').trim(),
      edge_function: String(formData.get('edge_function') ?? '').trim(),
      atualizado_em: new Date().toISOString(),
    };

    if (!data.nome || !data.schedule || !data.edge_function) {
      setError('Nome, agendamento e função edge são obrigatórios.');
      return;
    }

    const { error: err } = editJob.id
      ? await supabase.from('cron_jobs').update(data).eq('id', editJob.id)
      : await supabase.from('cron_jobs').insert({ ...data, ativo: true });

    if (err) {
      setError('Erro ao guardar: ' + err.message);
    } else {
      setMsg(editJob.id ? 'Tarefa atualizada!' : 'Tarefa criada!');
      setEditJob(null);
      setShowAdd(false);
      await fetchJobs();
      setTimeout(() => setMsg(''), 4000);
    }
  };

  const handleDelete = async (job: CronJob) => {
    if (!confirm(`Eliminar tarefa "${job.nome}"?`)) return;
    setError('');
    const { error: err } = await supabase.from('cron_jobs').delete().eq('id', job.id);
    if (err) {
      setError('Erro ao eliminar: ' + err.message);
    } else {
      setMsg('Tarefa eliminada.');
      await fetchJobs();
      setTimeout(() => setMsg(''), 4000);
    }
  };

  const formatSchedule = (schedule: string): string => {
    const map: Record<string, string> = {
      '0 0 1 * *': 'Todo o 1º dia do mês às 00:00',
      '0 9 * * 1': 'Todas as segundas-feiras às 09:00',
      '0 8 * * *': 'Todos os dias às 08:00',
    };
    return map[schedule] ?? schedule;
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'Nunca';
    return new Date(dateStr).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-zinc-100 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-zinc-900 rounded-xl flex-shrink-0">
              <i className="ri-time-line text-white text-lg" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-zinc-800">Tarefas Automáticas</h2>
              <p className="text-[11px] text-zinc-400 mt-0.5">Gerir e executar tarefas agendadas do sistema</p>
            </div>
          </div>
          <button
            onClick={() => { setEditJob({ id: '', nome: '', descricao: '', schedule: '0 8 * * *', edge_function: '', ativo: true, ultima_execucao: null, ultimo_resultado: null, ultimo_sucesso: null, criado_em: '' }); setShowAdd(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line text-sm" />
            </div>
            Nova Tarefa
          </button>
        </div>
      </div>

      {/* Feedback */}
      {msg && (
        <div className="flex items-center gap-2.5 p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-checkbox-circle-fill text-emerald-500 text-sm" />
          </div>
          <p className="text-[13px] text-emerald-700">{msg}</p>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-xl">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-error-warning-line text-red-500 text-sm" />
          </div>
          <p className="text-[13px] text-red-700">{error}</p>
        </div>
      )}

      {/* Lista de tarefas */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-14">
            <i className="ri-loader-4-line text-2xl text-zinc-300 animate-spin" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-14">
            <div className="w-10 h-10 flex items-center justify-center bg-zinc-100 rounded-full">
              <i className="ri-time-line text-zinc-400 text-lg" />
            </div>
            <p className="text-[13px] text-zinc-400">Nenhuma tarefa automática configurada</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-50">
            {jobs.map((job) => (
              <div key={job.id} className="px-6 py-4 hover:bg-zinc-50/60 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 ${job.ativo ? 'bg-emerald-100' : 'bg-zinc-100'}`}>
                      <i className={`${job.ativo ? 'ri-play-circle-line text-emerald-600' : 'ri-pause-circle-line text-zinc-400'} text-lg`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[13px] font-semibold text-zinc-800">{job.nome}</p>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${job.ativo ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-zinc-100 text-zinc-400 border border-zinc-200'}`}>
                          {job.ativo ? 'Ativa' : 'Inativa'}
                        </span>
                      </div>
                      {job.descricao && <p className="text-[11px] text-zinc-400 mt-0.5">{job.descricao}</p>}
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        <span className="flex items-center gap-1 text-[11px] text-zinc-400">
                          <div className="w-3 h-3 flex items-center justify-center"><i className="ri-calendar-line text-xs" /></div>
                          {formatSchedule(job.schedule)}
                        </span>
                        <span className="flex items-center gap-1 text-[11px] text-zinc-400">
                          <div className="w-3 h-3 flex items-center justify-center"><i className="ri-function-line text-xs" /></div>
                          {job.edge_function}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        <span className="text-[11px] text-zinc-400">
                          Última execução: {formatDate(job.ultima_execucao)}
                        </span>
                        {job.ultimo_sucesso !== null && (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${job.ultimo_sucesso ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                            {job.ultimo_sucesso ? 'Sucesso' : 'Falhou'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleExecute(job)}
                      disabled={executing === job.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white text-[11px] font-semibold rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                      title="Executar agora"
                    >
                      {executing === job.id ? (
                        <i className="ri-loader-4-line animate-spin text-xs" />
                      ) : (
                        <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-play-fill text-xs" /></div>
                      )}
                      Executar
                    </button>
                    <button
                      onClick={() => handleToggle(job)}
                      className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                      title={job.ativo ? 'Desativar' : 'Ativar'}
                    >
                      <i className={`${job.ativo ? 'ri-pause-line' : 'ri-play-line'} text-sm`} />
                    </button>
                    <button
                      onClick={() => { setEditJob(job); setShowAdd(true); }}
                      className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                      title="Editar"
                    >
                      <i className="ri-edit-line text-sm" />
                    </button>
                    <button
                      onClick={() => handleDelete(job)}
                      className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                      title="Eliminar"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Add/Edit */}
      {showAdd && editJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl border border-zinc-200 shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
              <h3 className="text-[15px] font-semibold text-zinc-800">
                {editJob.id ? 'Editar Tarefa' : 'Nova Tarefa'}
              </h3>
              <button onClick={() => { setShowAdd(false); setEditJob(null); }} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 rounded-lg transition-colors cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Nome</label>
                <input name="nome" defaultValue={editJob.nome} required className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" placeholder="Ex: Relatório Mensal" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Descrição</label>
                <input name="descricao" defaultValue={editJob.descricao ?? ''} className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" placeholder="Descrição da tarefa" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Agendamento (Cron)</label>
                <input name="schedule" defaultValue={editJob.schedule} required className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors font-mono" placeholder="0 8 * * *" />
                <p className="text-[11px] text-zinc-400 mt-1">Formato cron: minuto hora dia_mês mês dia_semana</p>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Edge Function</label>
                <input name="edge_function" defaultValue={editJob.edge_function} required className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" placeholder="nome-da-funcao" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { setShowAdd(false); setEditJob(null); }} className="px-4 py-2 text-[13px] font-medium text-zinc-600 hover:text-zinc-800 transition-colors cursor-pointer">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer">
                  {editJob.id ? 'Guardar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}